import { displayDate } from '@/lib/dates'
import type { OrganizationEngagement } from './organizationEngagement'
import { useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  SortableTableHead,
} from '@/components/ui/table'
import { getJSON } from '@/lib/api'
import { cn } from '@/lib/utils'
import { EngagementListFilters } from './EngagementListFilters'
import { columnsFor, listDefinitions, type EngagementRow } from './engagementListColumns'
import {
  changeEngagement,
  engagementListPath,
  useEngagementSearch,
  type EngagementListKey,
} from './engagementListState'

interface Category {
  key: string
  label: string
  value: { cents: number; formatted: string } | null
}
interface EngagementListResponse {
  rows: EngagementRow[]
  total: number
  next_cursor: string | null
  credits_total?: string
  categories?: Category[]
}
export function OrganizationEngagementList({
  contactId,
  tile,
  window,
}: {
  window: OrganizationEngagement['window']
  contactId: number
  tile: EngagementListKey
}) {
  const search = useEngagementSearch()
  const path = engagementListPath(contactId, tile, search)
  const query = useQuery({
    queryKey: ['engagement-list', path],
    queryFn: () => getJSON<EngagementListResponse>(path),
  })
  const params = new URLSearchParams(search)
  const definition = listDefinitions[tile]
  return (
    <Card>
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-wrap items-baseline gap-1.5">
            <h3 className="text-body font-medium">{definition.title}</h3>
            {query.data && (
              <span className="text-caption text-muted-foreground">
                · {query.data.total}
                {tile === 'courses_completed' && query.data.credits_total && (
                  <> · {Number(query.data.credits_total)} credits</>
                )}
              </span>
            )}
          </div>
          <Button asChild variant="outline" size="sm" className="gap-1.5">
            <a href={engagementListPath(contactId, tile, search, true)} download>
              <Download aria-hidden="true" className="size-3.5" />
              CSV
            </a>
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Input
            aria-label="Search"
            placeholder={definition.search}
            className="h-9 w-full sm:w-72"
            value={params.get('q') ?? ''}
            onChange={(event) => changeEngagement({ q: event.target.value })}
          />
          <EngagementListFilters tile={tile} params={params} />
        </div>
      </div>
      <ListPeriod window={window} tile={tile} previous={params.get('window') === 'previous'} />
      {query.isPending && <EmptyState title="Loading list" variant="quiet" />}
      {query.isError && (
        <Alert variant="destructive">
          List unavailable.{' '}
          <Button variant="link" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </Alert>
      )}
      {query.data && <ListContents data={query.data} tile={tile} params={params} />}
    </Card>
  )
}
function ListContents({
  data,
  tile,
  params,
}: {
  data: EngagementListResponse
  tile: EngagementListKey
  params: URLSearchParams
}) {
  const filtered = Array.from(params.keys()).some(
    (key) => ['q', 'person_status'].includes(key) || key.startsWith('filters['),
  )
  return (
    <>
      {tile === 'spend' && (
        <div
          role="group"
          aria-label="Category"
          className="flex flex-wrap items-center divide-x divide-border border-b border-border px-4 py-2"
        >
          <span className="pr-3 text-caption text-muted-foreground">Category</span>
          {data.categories?.map((category) => (
            <Button
              key={category.key}
              variant="ghost"
              size="sm"
              aria-pressed={params.get('filters[category]') === category.key}
              className={cn(
                'text-caption',
                params.get('filters[category]') === category.key && 'bg-primary/5',
              )}
              onClick={() =>
                changeEngagement({
                  'filters[category]':
                    params.get('filters[category]') === category.key ? '' : category.key,
                })
              }
            >
              <span className="text-muted-foreground">{category.label}</span>
              <span className="ml-1 tabular-nums">{category.value?.formatted ?? '—'}</span>
            </Button>
          ))}
        </div>
      )}
      {data.rows.length ? (
        <ListTable rows={data.rows} tile={tile} params={params} />
      ) : (
        <EmptyState
          variant="quiet"
          title={filtered ? 'No results match your filters' : 'No records yet'}
          action={
            filtered ? (
              <Button variant="outline" onClick={() => changeEngagement({}, true)}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      )}
      <ListFooter data={data} params={params} />
    </>
  )
}
function ListTable({
  rows,
  tile,
  params,
}: {
  rows: EngagementRow[]
  tile: EngagementListKey
  params: URLSearchParams
}) {
  const sort = params.get('sort') ?? listDefinitions[tile].sort
  const descending = sort.endsWith('_desc')
  const columns = columnsFor(tile, params)
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((column) =>
            column.sort ? (
              <SortableTableHead
                key={column.key}
                active={sort === `${column.sort}_asc` || sort === `${column.sort}_desc`}
                direction={descending ? 'descending' : 'ascending'}
                className={column.right ? 'text-right' : undefined}
                onToggle={() =>
                  changeEngagement({
                    sort: `${column.sort}_${sort === `${column.sort}_asc` ? 'desc' : 'asc'}`,
                  })
                }
              >
                {column.label}
              </SortableTableHead>
            ) : (
              <TableHead key={column.key}>{column.label}</TableHead>
            ),
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, index) => (
          <EngagementTableRow key={row.id ?? row.person_id ?? index} row={row} tile={tile}>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                className={column.right ? 'text-right tabular-nums' : undefined}
              >
                {column.cell(row)}
              </TableCell>
            ))}
          </EngagementTableRow>
        ))}
      </TableBody>
    </Table>
  )
}
function ListFooter({ data, params }: { data: EngagementListResponse; params: URLSearchParams }) {
  const offset = Number(params.get('offset') ?? 0)
  const start = data.total === 0 ? 0 : offset + 1
  return (
    <div className="flex items-center justify-between border-t border-border px-4 py-3">
      <span className="text-caption text-muted-foreground">
        {start}–{Math.min(offset + data.rows.length, data.total)} of {data.total}
      </span>
      <div className="flex gap-2">
        {params.has('cursor') && (
          <Button variant="outline" size="sm" onClick={() => changeEngagement({})}>
            First page
          </Button>
        )}
        {data.next_cursor && (
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              changeEngagement({
                cursor: data.next_cursor!,
                offset: String(offset + data.rows.length),
              })
            }
          >
            Next page
          </Button>
        )}
      </div>
    </div>
  )
}

function ListPeriod({
  window,
  tile,
  previous,
}: {
  window: OrganizationEngagement['window']
  tile: EngagementListKey
  previous: boolean
}) {
  if (tile === 'personal_memberships')
    return <div className="px-4 py-2 text-caption text-muted-foreground">As of today</div>
  if (!previous && window.current === null)
    return (
      <div className="px-4 py-2 text-caption text-muted-foreground">List window unavailable</div>
    )
  if (!previous) return null
  const period = window.same_point_previous
  return (
    <div className="px-4 py-2 text-caption text-muted-foreground">
      {period
        ? `List window · ${period.label} · ${displayDate(period.starts_on)} – ${displayDate(period.ends_on)}`
        : window.comparison_label}
    </div>
  )
}

function EngagementTableRow({
  row,
  tile,
  children,
}: {
  row: EngagementRow
  tile: EngagementListKey
  children: React.ReactNode
}) {
  const canOpen = tile === 'spend' || Boolean(row.person_id)
  const open = (element: HTMLTableRowElement) =>
    element.querySelector<HTMLAnchorElement>('a')?.click()
  return (
    <TableRow
      tabIndex={canOpen ? 0 : undefined}
      className={
        canOpen
          ? 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset'
          : undefined
      }
      onClick={(event) => {
        if (!(event.target as HTMLElement).closest('a, button')) open(event.currentTarget)
      }}
      onKeyDown={(event) => {
        if (event.target === event.currentTarget && ['Enter', ' '].includes(event.key)) {
          event.preventDefault()
          open(event.currentTarget)
        }
      }}
    >
      {children}
    </TableRow>
  )
}
